import { Link } from 'react-router-dom'
import type { StoredSession } from '../api/session.js'
import { LEGAL, LegalShell, Mail, TranslationNotice } from './LegalShell.js'

const NOTICE_BEFORE = 'Diese deutsche Übersetzung dient nur der Information. Bei Abweichungen ist die'
const NOTICE_LINK = 'französische Fassung'
const NOTICE_AFTER = 'maßgeblich.'

export function LegalNoticeDe({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Rechtliche Hinweise" title="Impressum">
      <TranslationNotice original="/mentions-legales" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />

      <h2>Herausgeber der Website</h2>
      <p>
        Die Website {LEGAL.domain} („{LEGAL.siteName}“) wird von einer Privatperson ohne gewerbliche Absicht herausgegeben. Gemäß Artikel 6
        III-2 des französischen Gesetzes Nr. 2004-575 vom 21. Juni 2004 über das Vertrauen in die digitale Wirtschaft veröffentlicht der
        Herausgeber seine persönlichen Kontaktdaten nicht; diese wurden dem Hosting-Anbieter mitgeteilt.
      </p>
      <p>
        Kontakt: <Mail />
      </p>

      <h2>Hosting</h2>
      <p>
        Die Website wird gehostet von {LEGAL.host.name}, {LEGAL.host.address.de} —{' '}
        <a href={LEGAL.host.website} rel="noopener noreferrer" target="_blank">
          {LEGAL.host.website.replace('https://', '')}
        </a>
        .
      </p>

      <h2>Geistiges Eigentum</h2>
      <p>
        Code, Illustrationen, Logo und Texte der Website sind Eigentum des Herausgebers, sofern nicht anders angegeben. Die Schach-Engine
        basiert auf der freien Bibliothek chess.js (Lizenz BSD-2-Clause); die Schriftarten Fraunces und Instrument Sans werden unter der SIL
        Open Font License verbreitet.
      </p>
      <p>
        Chess-Uno ist ein unabhängiges Spiel. Es ist weder mit Mattel, dem Inhaber der Marke UNO®, verbunden noch von Mattel genehmigt oder
        gesponsert.
      </p>

      <h2>Personenbezogene Daten</h2>
      <p>
        Wie die Daten der Spieler verarbeitet werden, beschreibt die <Link to="/de/datenschutz">Datenschutzerklärung</Link>.
      </p>
    </LegalShell>
  )
}

export function PrivacyDe({ session }: { session: StoredSession | null }) {
  return (
    <LegalShell session={session} eyebrow="Deine Daten" title="Datenschutzerklärung">
      <TranslationNotice original="/confidentialite" before={NOTICE_BEFORE} link={NOTICE_LINK} after={NOTICE_AFTER} />
      <p className="lead-p">
        {LEGAL.siteName} erhebt nur das unbedingt Nötige, damit du spielen und in der Rangliste geführt werden kannst. Keine Werbung, kein
        Reichweitenmessungs-Tool von Dritten, keine Tracking-Cookies.
      </p>

      <h2>Verantwortlicher</h2>
      <p>
        Der Herausgeber der Website, eine Privatperson (siehe <Link to="/de/impressum">Impressum</Link>), erreichbar unter <Mail />.
      </p>

      <h2>Verarbeitete Daten</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Daten</th>
              <th>Zweck</th>
              <th>Speicherdauer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Benutzername</td>
              <td>Dein Konto identifizieren und dich den anderen Spielern anzeigen.</td>
              <td>Bis zur Löschung des Kontos.</td>
            </tr>
            <tr>
              <td>Passwort</td>
              <td>Anmeldung. Es wird nie im Klartext gespeichert: Aufbewahrt wird nur ein gesalzener Hash (scrypt).</td>
              <td>Bis zur Löschung des Kontos.</td>
            </tr>
            <tr>
              <td>Sitzungstoken</td>
              <td>
                Angemeldet bleiben. Der Token liegt im lokalen Speicher deines Browsers; der Server bewahrt davon nur einen Hash auf.
              </td>
              <td>Bis zur Abmeldung oder zur Löschung des Kontos.</td>
            </tr>
            <tr>
              <td>Partien</td>
              <td>Züge, gezogene Karten und Protokoll jeder Partie, um nach einem Verbindungsabbruch fortzusetzen und Streitfälle zu klären.</td>
              <td>Bis zur Löschung des Kontos.</td>
            </tr>
            <tr>
              <td>Gewertete Ergebnisse</td>
              <td>Elo-Wertung, Statistiken, Bilanzen der direkten Duelle.</td>
              <td>Bis zur Löschung des Kontos, danach ohne deinen Benutzernamen aufbewahrt, um die Rangliste der anderen nicht zu verfälschen.</td>
            </tr>
            <tr>
              <td>Turniere</td>
              <td>Name des Turniers, Benutzernamen der Teilnehmer, Turnierbaum und Ergebnisse der Spiele, um das Turnier auszurichten.</td>
              <td>Bis zur Löschung des Kontos.</td>
            </tr>
            <tr>
              <td>Feedback und Vorschläge</td>
              <td>
                Was du senden möchtest: Nachricht, Bewertung von 1 bis 5, freiwillige Kontaktangabe (E-Mail oder Messenger-Name), außerdem dein
                Benutzername, falls du angemeldet bist, sowie die verwendete Seite, Sprache, Bildschirmart und der Browser. Vom Herausgeber gelesen,
                um das Spiel zu verbessern und dir zu antworten, wenn du darum bittest.
              </td>
              <td>12 Monate oder bis du die Löschung verlangst.</td>
            </tr>
            <tr>
              <td>Herkunft der Besuche</td>
              <td>
                Erfahren, ob Besucher von Instagram, Google oder anderswo kommen: verweisende Website (nur der Domainname), Kampagnen-Parameter des
                Links, Einstiegsseite ohne Parameter oder Einladungscodes, Sprache, Bildschirmart und ob der Besuch zur Erstellung eines Kontos
                geführt hat. Es werden weder IP-Adresse noch genaue Browserangaben oder eine dauerhafte Kennung gespeichert, und nichts wird mit
                deinem Konto verknüpft. Keine Messung, wenn dein Browser „Do Not Track“ oder das Signal Global Privacy Control sendet.
              </td>
              <td>13 Monate.</td>
            </tr>
            <tr>
              <td>IP-Adresse</td>
              <td>
                Begrenzung der Anmeldeversuche und der Feedback-Übermittlungen (Schutz vor Angriffen). Wird im Arbeitsspeicher gehalten, nie vom
                Spiel gespeichert.
              </td>
              <td>Eine Minute für die Anmeldung, zehn Minuten für Feedback. Die technischen Protokolle des Hosting-Anbieters folgen dessen eigenen Richtlinien.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Rechtsgrundlagen</h2>
      <ul>
        <li>
          <strong>Erbringung des Dienstes</strong>, den du durch das Erstellen eines Kontos anforderst: Konto, Partien, Rangliste.
        </li>
        <li>
          <strong>Berechtigtes Interesse</strong>: Sicherheit des Dienstes (Begrenzung der Anmeldeversuche und der Feedback-Übermittlungen),
          Verbesserung des Spiels anhand des Feedbacks, das du senden möchtest, und statistische Messung der Herkunft der Besuche. Du kannst dem
          jederzeit widersprechen, indem du „Do Not Track“ in deinem Browser aktivierst.
        </li>
      </ul>

      <h2>Was öffentlich ist</h2>
      <p>
        Dein Benutzername, deine Elo-Wertung und deine Stufe, deine Statistiken, deine gewerteten Ergebnisse und deine Bilanzen in direkten
        Duellen erscheinen in der Rangliste und auf deinem Profil und sind für alle sichtbar. Partien gegen die KI werden nicht gewertet. Der
        Name eines Turniers, die Benutzernamen seiner Teilnehmer, sein Turnierbaum und seine Ergebnisse sind für jeden sichtbar, der seinen
        Code kennt. Dein Feedback wird nie veröffentlicht.
      </p>

      <h2>Speicherung in deinem Browser</h2>
      <p>
        Die Website verwendet keine Cookies. Sie legt im lokalen Speicher des Browsers deinen Sitzungstoken ab, damit du angemeldet bleibst,
        dazu deine Anzeigeeinstellungen (Sprache, Ton) und einen kleinen Zähler, der verhindert, dass du nach einer Partie zu oft nach deiner
        Meinung gefragt wirst: Diese Speicherung ist für den Dienst unbedingt erforderlich und bedarf daher keiner Einwilligung. Der
        Sitzungsspeicher des Tabs bewahrt bis zu dessen Schließen eine zufällige Kennung des Besuchs auf, damit dieser nur einmal gezählt
        wird; sie wird nicht von einem Besuch zum nächsten aufbewahrt.
      </p>

      <h2>Empfänger und Auftragsverarbeiter</h2>
      <p>Deine Daten werden weder verkauft noch zu kommerziellen Zwecken weitergegeben. Sie werden verarbeitet von:</p>
      <ul>
        <li>
          <strong>{LEGAL.host.name}</strong> (Vereinigte Staaten): Hosting des Servers und der Datenbank;
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (Vereinigte Staaten): Verwaltung des Domainnamens und gegebenenfalls Weiterleitung des Datenverkehrs;
        </li>
        <li>
          <strong>Telegram</strong> (Messenger) und <strong>Resend, Inc.</strong> (Vereinigte Staaten, E-Mail-Versand), gegebenenfalls: Zustellung
          einer Benachrichtigung über jedes eingegangene Feedback an den Herausgeber.
        </li>
      </ul>
      <p>
        Diese Übermittlungen in Länder außerhalb der Europäischen Union sind durch die in der DSGVO vorgesehenen Garantien abgesichert
        (Standardvertragsklauseln der Europäischen Kommission oder EU-US-Datenschutzrahmen, je nach Anbieter).
      </p>

      <h2>Sicherheit</h2>
      <p>
        Verschlüsselte Verbindungen (HTTPS), Passwörter und Tokens als Hashes gespeichert, begrenzte Anmeldeversuche, Zugriff auf die Datenbank
        auf die Hosting-Umgebung beschränkt.
      </p>

      <h2>Deine Rechte</h2>
      <p>
        Du hast das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Um sie
        auszuüben — insbesondere um dein Konto zu löschen — schreib an <Mail /> von einer Adresse, über die du identifiziert werden kannst, und
        gib deinen Benutzernamen an. Du erhältst innerhalb eines Monats eine Antwort.
      </p>
      <p>
        Du kannst außerdem eine Beschwerde bei der CNIL, der französischen Datenschutzaufsichtsbehörde, einreichen (
        <a href="https://www.cnil.fr" rel="noopener noreferrer" target="_blank">
          cnil.fr
        </a>
        ).
      </p>
    </LegalShell>
  )
}
